"use client";

import type { SubmitEvent } from "react";
import type { FormStep } from "./form-types";

import { useCallback, useMemo, useRef } from "react";
import { stepHasSubmitButton } from "./form-step-utils";

/**
 * Every step shares one <form>; inactive steps are only hidden with CSS
 * (LeadFormStep), so the final step's submit button is always present in
 * the DOM and the browser's implicit submission (Enter, mobile "Go") would
 * activate it from any step. Only the step that actually renders a submit
 * button may submit; every other step's Enter becomes that step's own
 * Continue action instead, via the action registered through
 * KeyboardStepActionContext.
 */
function useLeadFormKeyboardSubmit(currentStep: FormStep) {
  // A ref, not state: handleSubmit only needs the latest value at submit time,
  // and holding it in state would re-render the whole form on every step change.
  const stepActionRef = useRef<(() => void) | null>(null);
  const registerKeyboardStepAction = useCallback(
    (action: (() => void) | null) => {
      stepActionRef.current = action;
    },
    [],
  );
  const keyboardStepActionContextValue = useMemo(
    () => ({ registerKeyboardStepAction }),
    [registerKeyboardStepAction],
  );

  const handleSubmit = useCallback(
    (event: SubmitEvent<HTMLFormElement>) => {
      if (stepHasSubmitButton(currentStep)) {
        return;
      }
      event.preventDefault();
      stepActionRef.current?.();
    },
    [currentStep],
  );

  return { handleSubmit, keyboardStepActionContextValue };
}

export { useLeadFormKeyboardSubmit };
